import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function KnowledgePortal() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [knownOnly, setKnownOnly] = useState(false);
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (category) params.set("category_key", category);
    if (knownOnly) params.set("known_errors_only", "true");
    apiFetch(`/knowledge/portal?${params}`).then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("Knowledge is unavailable right now"); setData(null); return; }
      setError(null); setData(body);
    });
  }, [search, category, knownOnly]);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const open = (id) =>
    apiFetch(`/knowledge/${id}`).then(async (r) => {
      if (!r.ok) { setStatus("That article is not available to you"); return; }
      setArticle(await r.json());
      setStatus(null);
    });

  const rate = (helpful) =>
    apiFetch(`/knowledge/${article.id}/feedback`, { method: "POST",
                                                     body: JSON.stringify({ helpful }) })
      .then((r) => setStatus(r.ok ? (helpful ? "Thank you — marked as helpful"
                                             : "Thank you — we will improve this article")
                                  : "Feedback could not be recorded"));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="knowledge-title">
            Knowledge Portal
          </h1>
          <p className="text-sm text-muted-foreground">
            Try this first — guidance and known errors, before you raise a request.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="knowledge-catalog-link">
            <Link to="/catalog">Raise a request</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {error && <p className="text-sm text-destructive" data-testid="knowledge-error">{error}</p>}
        {status && <p className="text-sm text-amber-800" data-testid="knowledge-status">{status}</p>}

        <section className="space-y-3" data-testid="knowledge-search">
          <form className="flex flex-col md:flex-row gap-2 md:items-end"
                onSubmit={(e) => { e.preventDefault(); setSearch(query); }}>
            <div className="flex-1 space-y-1">
              <label htmlFor="knowledge-query" className="text-sm font-medium">
                What do you need help with?
              </label>
              <Input id="knowledge-query" data-testid="knowledge-query" value={query}
                     placeholder="Password, printer, laptop, access…"
                     onChange={(e) => setQuery(e.target.value)} />
            </div>
            <Button type="submit" size="sm" data-testid="knowledge-search-submit">Search</Button>
          </form>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="knowledge-category" className="text-sm font-medium">Category</label>
              <select id="knowledge-category" data-testid="knowledge-category" value={category}
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      onChange={(e) => setCategory(e.target.value)}>
                <option value="">Every category</option>
                {(data?.categories || []).map((c) => (
                  <option key={c.key} value={c.key}>{c.name || c.key}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <input id="knowledge-known-only" type="checkbox" checked={knownOnly}
                     data-testid="knowledge-known-only"
                     onChange={(e) => setKnownOnly(e.target.checked)} />
              <label htmlFor="knowledge-known-only" className="text-sm">
                Known errors only (a fault we already know about)
              </label>
            </div>
          </div>
        </section>

        {data && (
          <section className="space-y-2" data-testid="knowledge-results">
            <h2 className="text-base md:text-lg font-medium">
              {data.totals.articles} article{data.totals.articles === 1 ? "" : "s"}
              {data.totals.known_errors > 0 && ` · ${data.totals.known_errors} known error(s)`}
            </h2>
            <p className="text-sm text-muted-foreground" data-testid="knowledge-method">
              {data.method}
            </p>
            <ul className="space-y-2">
              {data.articles.map((a) => (
                <li key={a.id}>
                  <button className="w-full text-left border rounded-md px-3 py-2 hover:border-foreground/40"
                          data-testid={`knowledge-result-${a.number}`} onClick={() => open(a.id)}>
                    <span className="text-sm font-medium">{a.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      <span className="font-mono">{a.number}</span> · {a.category_name}
                      {a.is_known_error && " · known error"}
                      {a.helpful > 0 && ` · ${a.helpful} found this helpful`}
                    </span>
                  </button>
                </li>
              ))}
              {data.articles.length === 0 && (
                <li className="text-sm text-muted-foreground" data-testid="knowledge-empty">
                  Nothing published matches that. Raise a request and someone will help.
                </li>
              )}
            </ul>
          </section>
        )}

        {article && (
          <section className="space-y-3 border-t pt-6" data-testid="knowledge-article">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-base md:text-lg font-medium" data-testid="knowledge-article-title">
                {article.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="knowledge-article-close"
                      onClick={() => setArticle(null)}>Close</Button>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="knowledge-article-meta">
              <span className="font-mono">{article.number}</span> · {article.category_key} ·{" "}
              {article.state}
              {article.review_date && ` · review due ${article.review_date}`}
            </p>
            <div className="text-sm whitespace-pre-wrap" data-testid="knowledge-article-body">
              {article.body}
            </div>
            {article.workaround && (
              <div className="border rounded-md p-3 text-sm" data-testid="knowledge-workaround">
                <h3 className="text-sm font-semibold">Workaround</h3>
                <p>{article.workaround}</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">Did this solve it?</span>
              <Button size="sm" variant="outline" data-testid="knowledge-helpful"
                      onClick={() => rate(true)}>Yes, thanks</Button>
              <Button size="sm" variant="outline" data-testid="knowledge-not-helpful"
                      onClick={() => rate(false)}>Not really</Button>
              <Button asChild size="sm" data-testid="knowledge-raise-request">
                <Link to="/catalog">Raise a request instead</Link>
              </Button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
